  import React from 'react';
  import { create } from 'zustand';
  import { Modal, ModalContent, ModalBody, ModalFooter } from '@chakra-ui/react';
  import { Sections } from 'src/legacy/Components/auth';
  import LoginForm from 'src/components/AuthForms/LoginForm';
  import RecoveryForm from 'src/components/AuthForms/RecoveryForm';
  import SignupForm from 'src/components/AuthForms/SignupForm';
  import { BodyBluredOverlay } from 'src/components/BodyBluredOverlay';
  import CompleteSingupForm from 'src/components/AuthForms/CompleteSingupForm';
  import { useUserStore } from 'src/hooks/stores';

  type AuthModalState = {
    //@ts-ignore
    defaultSection: Sections;
    isOpen: boolean;
    closeOnOverlayClick: boolean;
    onOverlayClick: (() => void) | null;
    onOpen: () => void;
    onOpenWithcloseOnOverlayClick: () => void;
    onLogin: () => void;
    onSignup: () => void;
    onClose: () => void;
    onCompleteSingup: () => void;
    setOnOverlayClick: (fn: () => void) => void;
    resolvers: Array<(response?: any) => void>;
    dummyResolver: null;
  };

  const initialState = {
    defaultSection: Sections.LOGIN,
    isOpen: false,
    closeOnOverlayClick: false,
    onOverlayClick: null,
  };

  export const useAuthModal = create<AuthModalState>((set, get) => ({
    ...initialState,
    dummyResolver: null,
    resolvers: [],
    onOpen: () => set({ isOpen: true }),
    onOpenWithcloseOnOverlayClick: () => set({ isOpen: true, closeOnOverlayClick: true }),
    onLogin: () => {
      const currentOnOverlayClick = get().onOverlayClick;
      set({
        defaultSection: Sections.LOGIN,
        isOpen: true,
        closeOnOverlayClick: true,
        onOverlayClick: currentOnOverlayClick || (() => get().onClose()),
      });
    },
    onSignup: () => {
      const currentOnOverlayClick = get().onOverlayClick;
      set({
        defaultSection: Sections.SIGNUP,
        isOpen: true,
        closeOnOverlayClick: true,
        onOverlayClick: currentOnOverlayClick || (() => get().onClose()),
      });
    },
    onCompleteSingup: () => {
      const currentOnOverlayClick = get().onOverlayClick;
      set({
        defaultSection: Sections.COMPLETE_SINGUP,
        isOpen: true,
        closeOnOverlayClick: true,
        onOverlayClick: currentOnOverlayClick,
      });
    },
    onClose: () => set(initialState),
    setOnOverlayClick: (fn) => set({ onOverlayClick: fn }),
  }));

  export const AuthModal = () => {
    const { defaultSection, isOpen, closeOnOverlayClick, onClose, resolvers, onOverlayClick } =
      useAuthModal();
    const [section, setSection] = React.useState(defaultSection);

    const _onClose = () => {
      if (useUserStore.getState().userStatus === 'pending') {
        useUserStore.getState().clear();
      }

      onClose();
      // TODO: switch after full migration to react-query.
      // dummyResolver?.();
      resolvers.forEach(resolve => {
        resolve(Response);
      });
    };

    React.useEffect(() => {
      setSection(defaultSection);
    }, [defaultSection, isOpen]);

    const handleKeyDown = () => {
      // Verifica se estamos em uma página de autenticação Google
      if (window.location.href.includes('accounts.google.com') ||
        (document.referrer && document.referrer.includes('accounts.google.com'))) {
        // Se estiver no fluxo do Google e tentar voltar, redireciona para home
        const savedOriginRoute = localStorage.getItem('auth_origin_route');
        window.location.href = savedOriginRoute || '/';
        return;
      }

      if (!window.location.href.includes('/what')) {
        onClose();
      }
    };

    React.useEffect(() => {
      if (!isOpen) return;

      window.addEventListener('popstate', handleKeyDown);

      return () => {
        window.removeEventListener('popstate', handleKeyDown);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    return (
      <>
        <BodyBluredOverlay
          isOpen={isOpen}
          onClick={() => {
            if (closeOnOverlayClick && onOverlayClick) {
              onOverlayClick();
            } else {
              _onClose();
            }
          }}
        />
        {isOpen && (
          <Modal
            onClose={_onClose}
            onEsc={onOverlayClick!}
            closeOnEsc={closeOnOverlayClick}
            isOpen={isOpen}
            isCentered
            motionPreset="slideInBottom"
            closeOnOverlayClick={closeOnOverlayClick}
            onOverlayClick={onOverlayClick!}
          >
            <ModalContent
              zIndex='2000'
            >
              <ModalBody pt={8}>
                {section === Sections.LOGIN && (
                  <LoginForm navigate={setSection} onLogIn={_onClose} />
                )}
                {section === Sections.SIGNUP && (
                  <SignupForm navigate={setSection} />
                )}
                {section === Sections.RECOVERY && (
                  <RecoveryForm navigate={setSection} />
                )}
                {section === Sections.COMPLETE_SINGUP && (
                  <CompleteSingupForm navigate={setSection} />
                )}
              </ModalBody>
              <ModalFooter />
            </ModalContent>
          </Modal>
        )}
      </>
    );
  };
